import { useEffect, useState } from 'react';
import { downloadFileName, looksLikePdf, unreadablePdfMessage } from '../../api/fileResponse';
import {
  downloadInvestorFileRendition,
  getInvestorFileCopyContext,
  listInvestorFileMembers,
} from '../../api/investorApi';
import { isZipSource } from '../../components/digitize/zipPdf';
import { pickZipMember } from '../../components/digitize/zipPdf';
import { saveBlob } from '../../components/custodian/saveBlob';

function Fact({ label, children, mono = false }) {
  return (
    <div className="min-w-0">
      <dt className="text-[10px] font-semibold uppercase tracking-wide text-eorig-muted">{label}</dt>
      <dd className={`mt-0.5 break-all text-xs text-eorig-ink ${mono ? 'font-mono' : ''}`}>
        {children == null || children === '' ? 'Not recorded' : children}
      </dd>
    </div>
  );
}

/**
 * Inline, read-only view of one package file for the Investor workspace.
 * Shows the watermarked labelled copy the vault issues for viewing, never
 * the preserved source bytes. Everything displayed comes from the backend.
 */
export function InvestorFileViewer({ packageId, file, onClose }) {
  const [context, setContext] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [mimeType, setMimeType] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [actionError, setActionError] = useState('');
  const [zipMember, setZipMember] = useState('');
  const [zipMembers, setZipMembers] = useState([]);

  const fileId = file?.id;
  const zipSource = isZipSource({ fileName: file?.file_name, mimeType: file?.mime_type });

  useEffect(() => {
    if (!packageId || !fileId) return undefined;
    let cancelled = false;

    async function loadMeta() {
      setError('');
      setActionError('');
      setContext(null);
      setZipMember('');
      setZipMembers([]);
      try {
        const nextContext = await getInvestorFileCopyContext(packageId, fileId);
        if (cancelled) return;
        setContext(nextContext);
        if (zipSource) {
          const listing = await listInvestorFileMembers(packageId, fileId);
          if (cancelled) return;
          const items = Array.isArray(listing?.items) ? listing.items : [];
          setZipMembers(items);
          if (!items.length) {
            setError('This ZIP does not contain any PDF files to view.');
            return;
          }
          setZipMember(items[0]);
        } else {
          setZipMember('');
        }
      } catch (requestError) {
        if (!cancelled) {
          setError(requestError.message || 'Unable to load the labelled copy.');
        }
      }
    }

    loadMeta();
    return () => {
      cancelled = true;
    };
  }, [packageId, fileId, zipSource]);

  useEffect(() => {
    if (!packageId || !fileId) return undefined;
    if (zipSource && !zipMember) return undefined;

    let cancelled = false;
    let objectUrl = null;

    async function loadRendition() {
      setLoading(true);
      setError('');
      setPreviewUrl(null);
      setMimeType('');
      try {
        const result = await downloadInvestorFileRendition(packageId, fileId, {
          disposition: 'inline',
          member: zipSource ? zipMember : undefined,
        });
        if (cancelled) return;
        if (!result?.blob) {
          setError('The labelled copy could not be loaded.');
          return;
        }
        const resolvedMime = result.mimeType || result.blob.type || 'application/octet-stream';
        if (resolvedMime === 'application/pdf' && !(await looksLikePdf(result.blob))) {
          if (!cancelled) setError(unreadablePdfMessage(result.blob.size));
          return;
        }
        if (cancelled) return;
        const viewBlob =
          result.blob.type === resolvedMime
            ? result.blob
            : new Blob([result.blob], { type: resolvedMime });
        objectUrl = URL.createObjectURL(viewBlob);
        setPreviewUrl(objectUrl);
        setMimeType(resolvedMime);
      } catch (requestError) {
        if (!cancelled) {
          setError(requestError.message || 'Unable to load the labelled copy.');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    loadRendition();
    return () => {
      cancelled = true;
      if (objectUrl && typeof URL.revokeObjectURL === 'function') {
        URL.revokeObjectURL(objectUrl);
      }
    };
  }, [packageId, fileId, zipSource, zipMember]);

  async function handleDownload() {
    setActionError('');
    try {
      let member = zipMember;
      if (zipSource && !member) {
        const listing = await listInvestorFileMembers(packageId, fileId);
        const items = Array.isArray(listing?.items) ? listing.items : [];
        member = items[0];
        if (!member) {
          setActionError('This ZIP does not contain any PDF files to download.');
          return;
        }
      }
      const result = await downloadInvestorFileRendition(packageId, fileId, {
        disposition: 'attachment',
        member,
      });
      if (!result?.blob) {
        setActionError('The labelled copy could not be loaded.');
        return;
      }
      saveBlob(
        result.blob,
        downloadFileName({
          fileName: result.fileName,
          label: context?.displayed_copy || file?.file_name,
          fallback: 'labelled-copy',
          mimeType: result.mimeType || result.blob.type,
        })
      );
    } catch (requestError) {
      setActionError(requestError.message || 'Unable to download the labelled copy.');
    }
  }

  if (!packageId || !fileId) return null;

  const isPdf = mimeType === 'application/pdf';

  return (
    <section
      className="rounded-2xl border border-eorig-border bg-white p-4 shadow-eorig-card"
      aria-label={`Viewing ${file?.file_name || 'file'}`}
    >
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <h2 className="truncate text-sm font-semibold text-eorig-ink" title={file?.file_name || ''}>
            {file?.file_name || 'File'}
          </h2>
          {context ? (
            <p className="mt-0.5 text-xs text-eorig-muted">
              You are viewing: <span className="font-semibold text-eorig-ink">{context.displayed_copy}</span>
              {' · '}Record: {context.record_status}
            </p>
          ) : null}
        </div>
        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            onClick={handleDownload}
            disabled={loading || Boolean(error)}
            className="rounded-full bg-eorig-navy-600 px-3 py-1.5 text-xs font-semibold text-white disabled:opacity-50"
          >
            Download labelled copy
          </button>
          {onClose ? (
            <button
              type="button"
              onClick={onClose}
              className="rounded-full border border-eorig-border bg-white px-3 py-1.5 text-xs font-semibold text-eorig-ink"
            >
              Close
            </button>
          ) : null}
        </div>
      </div>

      {context ? (
        <dl className="mt-3 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          <Fact label="Document custodian">{context.custodian_name}</Fact>
          <Fact label="Control party">{context.control_party}</Fact>
          <Fact label="Document ID" mono>
            {context.package_file_id}
          </Fact>
          <Fact label="Source SHA-256" mono>
            {context.source_sha256}
          </Fact>
        </dl>
      ) : null}
      <p className="mt-2 text-[11px] text-eorig-muted">
        The file on screen is a watermarked copy issued for viewing; the sealed vault record is never
        released from this workspace.
      </p>
      {zipSource && zipMembers.length > 1 ? (
        <label className="mt-2 flex flex-wrap items-center gap-2 text-xs text-eorig-muted">
          PDF in ZIP
          <select
            className="rounded border border-eorig-border bg-white px-2 py-1 text-xs text-eorig-ink"
            value={zipMember}
            onChange={(event) => setZipMember(event.target.value)}
            aria-label="PDF in ZIP"
          >
            {zipMembers.map((name) => (
              <option key={name} value={name}>
                {name}
              </option>
            ))}
          </select>
        </label>
      ) : null}
      {zipSource ? (
        <p className="mt-1 text-[11px] text-eorig-muted">
          Large batch ZIPs download one labelled PDF at a time, not the whole archive.
        </p>
      ) : null}

      {loading ? <p className="mt-3 text-sm text-eorig-muted">Loading labelled copy…</p> : null}
      {error ? (
        <p className="mt-3 text-sm text-red-600" role="alert">
          {error}
        </p>
      ) : null}
      {actionError ? (
        <p className="mt-2 text-sm text-red-600" role="alert">
          {actionError}
        </p>
      ) : null}
      {!loading && !error && previewUrl && isPdf ? (
        <iframe
          title={context?.displayed_copy || file?.file_name || 'Labelled copy'}
          src={previewUrl}
          className="mt-3 h-[70vh] min-h-[24rem] w-full rounded-xl border border-eorig-border bg-eorig-surface/40"
        />
      ) : null}
      {!loading && !error && previewUrl && !isPdf ? (
        <p className="mt-3 text-sm text-eorig-muted">
          Inline preview is not available for this file type. Download the labelled copy instead.
        </p>
      ) : null}
    </section>
  );
}
